import { Col } from '@zoom/components';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../app/AuthorizationProvider';
import { CalculatedForecastData, CalculatedForecastValuesData } from '../../../graphql/components';
import { RecordBooleanType } from '../../../types';
import { BASE_SERIES_ID, ChartTypes, GAPS_SERIES_ID, IChartConfig, createForecastChartsConfig } from '../config';
import { Chart } from './Chart';
import { ComposedChart, ComposedChartDataEntry } from './ComposedChart';

import type { AdjustmentRangeType } from './Chart';

interface FormConfigureForecastChartsProps {
  adjustmentsInCollision: RecordBooleanType;
  currentAdjustmentRange: AdjustmentRangeType;
  endDateTime: number;
  forecastData: CalculatedForecastData;
  hoveredAdjustmentId: string | null;
  isSidebarOpen: boolean;
  previewedAdjustments: RecordBooleanType;
  timezone: string;
}

export const FormConfigureForecastCharts: React.FC<FormConfigureForecastChartsProps> = ({
  adjustmentsInCollision,
  currentAdjustmentRange,
  endDateTime,
  forecastData,
  isSidebarOpen,
  hoveredAdjustmentId,
  previewedAdjustments,
  timezone,
}) => {
  const { localeObj } = useAuth();

  const { t } = useTranslation();

  const fullFormatDate = t('date.formats.full');
  const shortFormatDate = t('date.formats.shortened');

  return (
    <>
      {Object.values(createForecastChartsConfig).map((chartConfig: IChartConfig) => {
        const yAxisLabel = t(chartConfig.chartProps.yAxisLabel);
        const titleOfChart = t(chartConfig.title.dataKey);

        const commonChartProps = {
          dateFormat: { full: fullFormatDate, short: shortFormatDate },
          dataTestID: chartConfig.dataTestID,
        };

        if (chartConfig.type === ChartTypes.COMPOSED) {
          const composedSeriesOptions: ComposedChartDataEntry[] = [];

          Object.values(chartConfig.chartProps.entries).forEach((entry) => {
            const composedChartData: CalculatedForecastValuesData[] =
              forecastData[entry.mappedProp]?.forecastValues || [];
            const composedGapsData = forecastData[entry.mappedProp]?.gaps || [];
            const nameOfData = t(entry.name);
            const baseSeriesId = `${entry.mappedProp}_${BASE_SERIES_ID}`;

            composedChartData.forEach(({ adjustmentId, data: seriesData }) => {
              composedSeriesOptions.push({
                adjustmentId,
                baseSeriesId,
                data: seriesData,
                id: adjustmentId ? `${entry.mappedProp}_${adjustmentId}` : baseSeriesId,
                inPreview:
                  !!adjustmentId &&
                  (hoveredAdjustmentId === adjustmentId ||
                    (previewedAdjustments && !!previewedAdjustments[adjustmentId])),
                inCollision: !!adjustmentId && adjustmentsInCollision && !!adjustmentsInCollision[adjustmentId],
                ...entry,
                name: nameOfData,
                nameKey: entry.name,
                useGapConfig: false,
              });
            });

            if (composedGapsData.length) {
              composedSeriesOptions.unshift({
                baseSeriesId,
                adjustmentId: undefined,
                data: composedGapsData,
                id: `${entry.mappedProp}_${GAPS_SERIES_ID}`,
                inCollision: false,
                inPreview: false,
                useGapConfig: true,
                ...entry,
                name: nameOfData,
                nameKey: entry.name,
              });
            }
          });

          return (
            <Col key={chartConfig.key} sm={{ size: 12 }}>
              <p className={chartConfig.title.className} data-test-id={chartConfig.title.dataTestID}>
                {titleOfChart}
              </p>
              <ComposedChart
                {...commonChartProps}
                endDateTime={endDateTime}
                seriesOptions={composedSeriesOptions}
                highlightedRange={currentAdjustmentRange}
                isSidebarOpen={isSidebarOpen}
                labels={{
                  xAxis: t('date.date'),
                  yAxis: yAxisLabel,
                }}
                timezone={timezone}
                localeObj={localeObj}
              />
            </Col>
          );
        }

        const data: CalculatedForecastValuesData[] =
          forecastData[chartConfig.chartProps.mappedProp]?.forecastValues || [];
        const gapsData = forecastData[chartConfig.chartProps.mappedProp]?.gaps || [];

        const seriesOptions = data.map(({ adjustmentId, data: seriesData }) => ({
          adjustmentId,
          data: seriesData,
          inCollision: !!adjustmentId && adjustmentsInCollision && !!adjustmentsInCollision[adjustmentId],
          inPreview:
            !!adjustmentId &&
            (hoveredAdjustmentId === adjustmentId || (previewedAdjustments && !!previewedAdjustments[adjustmentId])),
          useGapConfig: false,
        }));

        if (gapsData.length) {
          seriesOptions.unshift({
            adjustmentId: undefined,
            data: gapsData,
            inCollision: false,
            inPreview: false,
            useGapConfig: true,
          });
        }

        return (
          <Col key={chartConfig.chartProps.mappedProp} sm={{ size: 12 }}>
            <p className={chartConfig.title.className} data-test-id={chartConfig.title.dataTestID}>
              {titleOfChart}
            </p>
            <Chart
              {...commonChartProps}
              endDateTime={endDateTime}
              highlightedRange={currentAdjustmentRange}
              id={chartConfig.chartProps.mappedProp}
              isSidebarOpen={isSidebarOpen}
              labels={{
                xAxis: t('date.date'),
                yAxis: yAxisLabel,
              }}
              name={t(chartConfig.chartProps.nameKey)}
              nameKey={chartConfig.chartProps.nameKey}
              seriesOptions={seriesOptions}
              timezone={timezone}
              localeObj={localeObj}
            />
          </Col>
        );
      })}
    </>
  );
};
